import { z } from 'zod';
import type { Severity } from './nivision';
import { ParamValueSchema, TRI_STATES, type ParamValue, type RuleKnob, type SettingSource, type TriState } from './rule-settings';

/**
 * Wave 3 (D-131): the client's own settings page on the portal - the DTOs of `/api/p/settings/*` (and the app bridge
 * `/internal/portal/settings/*`), the opt-in texts and reply parser of a client-added contact, and the "only to the contact"
 * recipient helpers over a client's own alerts route (1A S5-16, D-92 / D-101).
 */

export const PORTAL_LIMITS = { optInPerClientPerDay: 10, maxPendingPerClient: 10, optInDays: 7, nameMin: 2, nameMax: 40 } as const;

export type OptInStatus = 'pending' | 'confirmed' | 'declined' | 'expired' | 'withdrawn' | 'cancelled';

export interface PortalStats {
  /** local dates (YYYY-MM-DD, Asia/Jerusalem), inclusive */
  from: string;
  to: string;
  days: number;
  total: number;
  open: number;
  inProgress: number;
  handled: number;
  notRelevant: number;
  /** round(100 × (handled + notRelevant) / total); null when total = 0 */
  closedPct: number | null;
  /** count desc, then name (he) */
  byType: { ruleId: string; name: string; count: number; closed: number }[];
  /** every day of the window, ascending */
  byDay: { date: string; count: number }[];
  /** count desc, then name */
  byBranch: { siteId: string; clientId: string; name: string; count: number; closed: number; closedPct: number | null }[];
}

/** W2-1's Produced API `ScopeRule`, field list copied verbatim (the api's own type lives in alerts/settings). */
export interface PortalRuleBase {
  ruleId: string;
  name: string;
  kind: 'call' | 'watch';
  source: 'car' | 'general' | 'labs' | 'retail' | 'insurance' | 'nivision' | 'watch';
  on: boolean;
  severity: Severity;
  inheritedOn: boolean;
  inheritedSeverity: Severity;
  inheritedSource: SettingSource;
  onSource: SettingSource;
  severitySource: SettingSource;
  own: TriState;
  ownSeverity: Severity | null;
  ownConfig: Record<string, ParamValue> | null;
  severityEditable: boolean;
  checks: string | null;
  firesWhen: string[];
  notWhen: string[];
  action: string | null;
  description: string | null;
  fieldLabel: string | null;
  knobs: RuleKnob[];
  config: Record<string, ParamValue>;
  configDefaults: Record<string, ParamValue>;
  fires7d: number;
  fires30d: number;
  overrides: { clientId: string; clientName: string; what: ('on' | 'severity' | 'config')[] }[];
  siteOverride: boolean;
  receivers: { clientsOn: number; clientsTotal: number; network: boolean; contactOnly: number; nowhere: number } | null;
  /* extra */
  defaultEnabled: boolean | null;
  defaultSeverity: Severity;
  exposure: { toggle: boolean; knobs: Record<string, { min?: number; max?: number }> };
}

/** a rule on the portal; `managedByUs` = NIVISION already set something for this client and rule (read-only there) */
export type PortalRule = PortalRuleBase & { managedByUs: boolean };

export interface PortalRulesResponse {
  clientId: string;
  label: string;
  names: { segment: null; parent: string | null };
  rules: PortalRule[];
}

export interface PortalRecipientsView {
  clientId: string;
  /** none = no client-level alerts route with a group */
  route: 'ok' | 'none';
  /** the main group (first group target) */
  group: { id: string; name: string } | null;
  contacts: { contactId: string; name: string; phoneMasked: string | null; editable: boolean }[];
  /** the parent's route exists (read-only) */
  network: { name: string } | null;
  /** site-level alerts routes (read-only) */
  siteRoutes: { siteName: string }[];
  rules: { ruleId: string; name: string; on: boolean; group: boolean; contactIds: string[]; nowhere: boolean }[];
}

export interface PortalRecipientsPut {
  ruleId: string;
  group: boolean;
  contactIds: string[];
}

export interface PortalContact {
  id: string;
  name: string;
  phoneMasked: string;
  status: OptInStatus;
  ruleIds: string[];
  only: boolean;
  requestedBy: string;
  createdAt: string;
  answeredAt: string | null;
  expiresAt: string;
}

export interface PortalContactAdd {
  name: string;
  phone: string;
  ruleIds: string[];
  only: boolean;
}

export interface PortalSafetyView {
  status: 'draft' | 'active' | 'paused';
  /** kill switch: ours (global) or this client's */
  paused: 'none' | 'global' | 'client';
  quietHours: { enabled: boolean; start: string; end: string; shabbatAndHolidays: boolean; criticalBypass: boolean };
  /** D-134 (W3-5-4): the effective minutes until the reminder of an unmarked critical alert (0 = off); read-only on the portal in v1 */
  remindAfterMin?: number;
}

export interface PortalSettingsOverview {
  client: { id: string; name: string; kind: 'client' | 'parent' };
  /** the write targets: [itself] for a client, its children for a parent */
  branches: { clientId: string; name: string }[];
  canManage: boolean;
  displayName: string;
  safety: PortalSafetyView;
}

export interface PortalChangeItem {
  id: string;
  at: string;
  actorName: string;
  clientName: string;
  action: string;
  lines: string[];
}

// ---------- request bodies (validated by the portal controller AND the app bridge - one definition) ----------

const ruleIdSchema = z.string().min(1).max(64);

export const PortalRulesPutSchema = z
  .object({
    rules: z
      .record(
        ruleIdSchema,
        z.object({ on: z.enum(TRI_STATES).optional(), config: z.record(z.string(), ParamValueSchema).nullable().optional() }).strict(),
      )
      .refine((r) => Object.keys(r).length >= 1 && Object.keys(r).length <= 20, { message: 'בחרו בין התראה אחת ל-20' }),
  })
  .strict();
export type PortalRulesPut = z.infer<typeof PortalRulesPutSchema>;

export const PortalRecipientsPutSchema = z
  .object({ ruleId: ruleIdSchema, group: z.boolean(), contactIds: z.array(z.string().min(1).max(64)).max(20) })
  .strict();

/**
 * R23: the name goes into OUR outgoing WhatsApp text ("👋 שלום {name}") to a number the client typed, so it is a name and nothing
 * else: letters (any script, with their combining marks), spaces and ' - . only; at least one letter; no digits, no URL / domain,
 * no * _ ~ markdown.
 */
export const CONTACT_NAME_RE = /^[\p{L}\p{M}' .-]+$/u;
export const CONTACT_NAME_MESSAGE = "השם יכול להכיל רק אותיות, רווחים והסימנים ' - . (בלי ספרות וקישורים)";

/** NFC; whitespace controls become spaces; other C0 controls, DEL and bidi controls are dropped; spaces collapsed; trimmed */
export function cleanContactName(raw: string): string {
  return Array.from(raw.normalize('NFC').replace(/[\t\n\v\f\r]/g, ' '))
    .filter((ch) => !isDroppedChar(ch.codePointAt(0)!))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** C0 controls, DEL and the bidi controls (U+200E/F, U+202A–E, U+2066–9) */
function isDroppedChar(cp: number): boolean {
  return cp <= 0x1f || cp === 0x7f || cp === 0x200e || cp === 0x200f || (cp >= 0x202a && cp <= 0x202e) || (cp >= 0x2066 && cp <= 0x2069);
}

/** 2–40 characters, letters / spaces / ' - . only, at least one letter, and nothing that looks like a domain (evil.com) */
export function isValidContactName(name: string): boolean {
  const len = Array.from(name).length;
  if (len < PORTAL_LIMITS.nameMin || len > PORTAL_LIMITS.nameMax) return false;
  return CONTACT_NAME_RE.test(name) && /\p{L}/u.test(name) && !/[A-Za-z]\.[A-Za-z]/.test(name);
}

export const PortalContactAddSchema = z
  .object({
    name: z.string().max(80).transform(cleanContactName).refine(isValidContactName, { message: CONTACT_NAME_MESSAGE }),
    phone: z.string().trim().min(9).max(20),
    ruleIds: z.array(z.string().min(1).max(64)).min(1).max(30),
    only: z.boolean().default(true),
  })
  .strict();

// ---------- opt-in: the reply parser and the WhatsApp texts (every line starts with an emoji) ----------

const YES_WORDS = new Set(['כן', 'yes', 'מאשר', 'מאשרת', 'מאשרים']);
const NO_WORDS = new Set(['לא', 'no']);
const STOP_WORDS = new Set(['הסר', 'הסירו', 'stop', 'unsubscribe']);

/** a short reply of 1–3 words whose first word is a yes / no / stop word; anything else is null (changes nothing) */
export function optInReply(body: string | null | undefined): 'yes' | 'no' | 'stop' | null {
  if (!body) return null;
  const words = body
    .toLowerCase()
    .replace(/[^a-zא-ת ]+/g, ' ')
    .split(' ')
    .filter(Boolean);
  if (words.length < 1 || words.length > 3) return null;
  const first = words[0]!;
  if (YES_WORDS.has(first)) return 'yes';
  if (NO_WORDS.has(first)) return 'no';
  if (STOP_WORDS.has(first)) return 'stop';
  return null;
}

export function optInRequestText(p: { contactName: string; clientName: string }, lang: 'he' | 'en'): string {
  if (lang === 'en') {
    return [
      `👋 Hi ${p.contactName},`,
      `🏢 ${p.clientName} asked to send you WhatsApp alerts about customer calls, via NIVISION.`,
      `✅ Reply *yes* to receive alerts from ${p.clientName}.`,
      '🔕 If you did not ask for this, do nothing — we will not message you again.',
    ].join('\n');
  }
  return [
    `👋 שלום ${p.contactName},`,
    `🏢 ${p.clientName} ביקשו לשלוח לך בוואטסאפ התראות על שיחות עם לקוחות, דרך NIVISION.`,
    `✅ השיבו *כן* כדי לקבל התראות מ-${p.clientName}.`,
    '🔕 אם לא ביקשתם - אין צורך לעשות דבר, ולא נשלח אליך הודעות נוספות.',
  ].join('\n');
}

export function optInConfirmedText(p: { contactName: string; clientName: string }, lang: 'he' | 'en'): string {
  if (lang === 'en') return `✅ Thanks ${p.contactName}! You will now receive alerts from ${p.clientName}.\n🔕 To stop at any time, reply *stop*.`;
  return `✅ תודה ${p.contactName}! מעכשיו יגיעו אליך התראות מ-${p.clientName}.\n🔕 כדי להפסיק בכל רגע, כתבו *הסר*.`;
}

export function optInWithdrawnText(p: { clientName: string }, lang: 'he' | 'en'): string {
  if (lang === 'en') return `👍 Done. You will not receive more alerts from ${p.clientName}.`;
  return `👍 הסרנו אותך. לא יישלחו אליך עוד התראות מ-${p.clientName}.`;
}

// ---------- recipients on a client's own alerts route ----------

export interface RouteTargetLike {
  kind: 'group' | 'contact';
  id: string;
  ruleUuids?: string[];
  excludeRuleUuids?: string[];
}

const dedupe = (xs: readonly string[]): string[] => [...new Set(xs)];
const sameList = (a: readonly string[] | undefined, b: readonly string[]): boolean =>
  a !== undefined && a.length === b.length && a.every((x, i) => x === b[i]);

/** D-92 / D-101: a target wants a rule when its allow list is empty or holds it, and its exclude list does not */
function wants(t: RouteTargetLike, ruleId: string): boolean {
  const allow = t.ruleUuids ?? [];
  return (allow.length === 0 || allow.includes(ruleId)) && !(t.excludeRuleUuids ?? []).includes(ruleId);
}

const mainGroupIndex = (targets: readonly RouteTargetLike[]): number => targets.findIndex((t) => t.kind === 'group');

/** the target with a new exclude list; the same object when the list is already exactly that */
function withExclude<T extends RouteTargetLike>(t: T, exclude: string[]): T {
  return sameList(t.excludeRuleUuids, exclude) ? t : { ...t, excludeRuleUuids: exclude };
}

/** who gets ruleId on a client's own alerts route; group = the first group target */
export function ruleRecipientsOf(targets: readonly RouteTargetLike[], ruleId: string): { group: boolean; contactIds: string[]; nowhere: boolean } {
  const gi = mainGroupIndex(targets);
  const group = gi >= 0 && wants(targets[gi]!, ruleId);
  const contactIds = targets.filter((t) => t.kind === 'contact' && wants(t, ruleId)).map((t) => t.id);
  const nowhere = !targets.some((t) => wants(t, ruleId));
  return { group, contactIds, nowhere };
}

/**
 * Sets who gets ruleId: the main group (via its exclude list) and each editable contact (via its allow / exclude list). A contact
 * whose allow list would become empty is dropped (empty = every alert). Every other target is returned as the same object, so a
 * contact-only route of another rule stays byte-for-byte unchanged.
 */
export function applyRuleRecipients<T extends RouteTargetLike>(
  targets: readonly T[],
  ruleId: string,
  choice: { group: boolean; contactIds: readonly string[] },
  editable: ReadonlySet<string>,
): T[] {
  const gi = mainGroupIndex(targets);
  const chosen = new Set(choice.contactIds);
  const out: T[] = [];
  targets.forEach((t, i) => {
    if (i === gi) {
      const ex = dedupe(t.excludeRuleUuids ?? []);
      out.push(withExclude(t, choice.group ? ex.filter((x) => x !== ruleId) : ex.includes(ruleId) ? ex : [...ex, ruleId]));
      return;
    }
    if (t.kind !== 'contact' || !editable.has(t.id)) {
      out.push(t);
      return;
    }
    const allow = dedupe(t.ruleUuids ?? []);
    const ex = dedupe(t.excludeRuleUuids ?? []);
    if (chosen.has(t.id)) {
      const nextAllow = allow.length && !allow.includes(ruleId) ? [...allow, ruleId] : allow;
      const nextEx = ex.filter((x) => x !== ruleId);
      if (sameList(t.ruleUuids ?? [], nextAllow) && sameList(t.excludeRuleUuids, nextEx)) out.push(t);
      else out.push({ ...t, ...(t.ruleUuids !== undefined || nextAllow.length ? { ruleUuids: nextAllow } : {}), excludeRuleUuids: nextEx });
      return;
    }
    if (allow.length) {
      if (!allow.includes(ruleId)) {
        out.push(t);
        return;
      }
      const rest = allow.filter((x) => x !== ruleId);
      if (rest.length) out.push({ ...t, ruleUuids: rest });
      return; // the last type left: the target is dropped
    }
    // gets everything: exclude this one
    out.push(ex.includes(ruleId) ? t : { ...t, excludeRuleUuids: [...ex, ruleId] });
  });
  return out;
}

/**
 * Adds a contact with its types (an existing target of the same contact is replaced). No types = nothing changes.
 * `only` = the main group stops getting those types.
 */
export function addContactTarget<T extends RouteTargetLike>(targets: readonly T[], contact: T, ruleIds: readonly string[], only: boolean): T[] {
  if (!ruleIds.length) return [...targets];
  const ids = dedupe(ruleIds);
  const gi = mainGroupIndex(targets);
  const out: T[] = [];
  targets.forEach((t, i) => {
    if (t.kind === 'contact' && t.id === contact.id) return;
    if (i === gi && only) {
      const ex = dedupe(t.excludeRuleUuids ?? []);
      out.push(withExclude(t, [...ex, ...ids.filter((x) => !ex.includes(x))]));
      return;
    }
    out.push(t);
  });
  out.push({ ...contact, ruleUuids: ids, excludeRuleUuids: [] });
  return out;
}

/** Drops a contact; each type it held goes back to the main group unless another remaining target still holds it (1A S5-16). */
export function removeContactTarget<T extends RouteTargetLike>(targets: readonly T[], contactId: string): T[] {
  const removed = targets.find((t) => t.kind === 'contact' && t.id === contactId);
  if (!removed) return [...targets];
  const rest = targets.filter((t) => t !== removed);
  const gi = mainGroupIndex(rest);
  const stillHeld = new Set(rest.filter((_, i) => i !== gi).flatMap((t) => t.ruleUuids ?? []));
  const giveBack = new Set((removed.ruleUuids ?? []).filter((id) => !stillHeld.has(id)));
  if (gi < 0 || !giveBack.size) return rest;
  const g = rest[gi]!;
  const ex = dedupe(g.excludeRuleUuids ?? []);
  rest[gi] = withExclude(g, ex.filter((x) => !giveBack.has(x)));
  return rest;
}
